import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  useGetQuizAttemptsQuery,
  useGetAttemptByIdQuery,
} from "../../slices/quizAttemptApiSlice";

import AttemptReportTable from "./components/AttemptReportTable";

import StudentAttemptDetailsModal from "./components/StudentAttemptDetailsModal";

const QuizAttemptReportScreen = () => {
  const { quizId } = useParams();

  const [selectedAttemptId, setSelectedAttemptId] = useState(null);

  // Load all attempts for quiz
  const { data, isLoading, isError } = useGetQuizAttemptsQuery(quizId);

  const attempts = Array.isArray(data) ? data : data?.attempts || [];

  // Load selected attempt details
  const { data: attemptDetails, isLoading: isDetailsLoading } =
    useGetAttemptByIdQuery(selectedAttemptId, {
      skip: !selectedAttemptId,
    });

  const handleViewAttempt = (attemptId) => {
    if (!attemptId) return;

    setSelectedAttemptId(attemptId);
  };

  const handleCloseModal = () => {
    setSelectedAttemptId(null);
  };

  const passedAttempts = attempts.filter(
    (item) => item.result === "pass",
  ).length;

  const failedAttempts = attempts.filter(
    (item) => item.result === "fail",
  ).length;

  const averageScore =
    attempts.length > 0
      ? (
          attempts.reduce((total, item) => total + (item.percentage || 0), 0) /
          attempts.length
        ).toFixed(2)
      : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading quiz report...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">
            Unable to load quiz report
          </h2>

          <p className="text-gray-500 mt-2">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}

        <div className="bg-white rounded-lg border p-5">
          <h1 className="text-2xl font-bold text-gray-800">
            Quiz Attempt Report
          </h1>

          <p className="text-gray-500 mt-1">
            View student performance and attempt details
          </p>
        </div>

        {/* Summary */}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Attempts</p>

            <p className="text-2xl font-bold text-gray-800">
              {attempts.length}
            </p>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-500">Passed</p>

            <p className="text-2xl font-bold text-green-600">
              {passedAttempts}
            </p>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-500">Failed</p>

            <p className="text-2xl font-bold text-red-600">{failedAttempts}</p>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-500">Average Score</p>

            <p className="text-2xl font-bold text-blue-600">{averageScore}%</p>
          </div>
        </div>

        {/* Report Table */}

        <AttemptReportTable
          attempts={attempts}
          onViewAttempt={handleViewAttempt}
        />
      </div>

      {/* Details Modal */}

      <StudentAttemptDetailsModal
        isOpen={Boolean(selectedAttemptId)}
        onClose={handleCloseModal}
        attempt={isDetailsLoading ? null : attemptDetails}
      />
    </div>
  );
};

export default QuizAttemptReportScreen;
